import { FC, useEffect, useState } from "react";
import moment from "moment";
import FlightDetails from "../(components)/FlightDetails";
import { createPaymentPlanOrder, PaymentPlanOrderType } from "../../../data/paymentPlan/_requests";
import { paymentPlanValidationSchema } from "../../../utils/paymentPlanValidation";
import { initiateFlightDealCheckout } from "../../../data/payment/_requests";

import ButtonPrimary from "shared/Button/ButtonPrimary";
import Checkbox from "shared/Checkbox/Checkbox";
import Select from "shared/Select/Select";

// Simple UUID alternative for browser compatibility
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

interface SidebarProps {
  flightDealBookingPending: any;
  currency: string;
  exchangeRate: number;
  isCreatingCustomer: boolean;
  useFormikContext: any;
  handleTermsSelection: any;
  terms: boolean;
  ErrorMessage: any;
  paymentType?: string;
  onPaymentTypeChange?: (value: string) => void;
}

const Sidebar: FC<SidebarProps> = ({
  flightDealBookingPending,
  currency,
  exchangeRate,
  isCreatingCustomer,
  useFormikContext,
  terms,
  handleTermsSelection,
  ErrorMessage,
  paymentType: propPaymentType,
  onPaymentTypeChange,
}) => {
  const [surcharge, setSurcharge] = useState<string>("");
  const totalPriceAud = flightDealBookingPending.total_price;
  const [subTotalPrice, setSubTotalPrice] = useState<string>("");
  const [totalPrice, setTotalPrice] = useState<string>("");
  const [paymentType, setPaymentType] = useState<string>(propPaymentType || "full");
  const [depositAmount, setDepositAmount] = useState<string>("");
  const [depositError, setDepositError] = useState<string>("");
  const [weekOptions, setWeekOptions] = useState<number[]>([2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]);
  const [selectedNumberOfWeeks, setSelectedNumberOfWeeks] = useState<number>(2);
  const [isSubmittingPaymentPlan, setIsSubmittingPaymentPlan] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [departureDate, setDepartureDate] = useState<string>(
    flightDealBookingPending?.flightDeal?.departure_datetime 
      ? moment(flightDealBookingPending.flightDeal.departure_datetime).format('YYYY-MM-DD')
      : ""
  );

  const [subTotalAmountReserveDeal, setSubTotalAmountReserveDeal] =
    useState<string>("");
  const [totalReserveAmountWithSurcharge, setTotalReserveAmountWithSurcharge] =
    useState<string>("");
  const [reserveDealSurcharge, setReserveDealSurcharge] = useState<string>("");

  const surcharge_percentage_aud: number = parseFloat(
    process.env.REACT_APP_COMMWEB_SURCHARGE || "0"
  );
  const surcharge_percentage_lkr: number = parseFloat(
    process.env.REACT_APP_COMMERCIAL_SURCHARGE || "0"
  );

  useEffect(() => {
    let totalAmount = Number(totalPriceAud);
    if (currency === "LKR") {
      totalAmount = totalPriceAud * exchangeRate;
    }

    let currentSurcharge = 0;
    if (currency === "AUD") {
      currentSurcharge = Number(totalAmount) * (surcharge_percentage_aud / 100);
    } else if (currency === "LKR") {
      currentSurcharge = Number(totalAmount) * (surcharge_percentage_lkr / 100);
    }

    const totalWithSurcharge = Number(totalAmount) + Number(currentSurcharge);

    setSubTotalPrice(totalAmount.toFixed(2));

    setTotalPrice(totalWithSurcharge.toFixed(2));

    setSurcharge(currentSurcharge.toFixed(2));

    if (flightDealBookingPending.flightDeal.allow_reserve_deal) {
      let totalAmountReserveDeal = Number(
        flightDealBookingPending.flightDeal.reserve_deal_price
      );
      if (currency === "LKR") {
        totalAmountReserveDeal =
          flightDealBookingPending.flightDeal.reserve_deal_price * exchangeRate;
      }

      let reserveDealSurcharge = 0;
      if (currency === "AUD") {
        reserveDealSurcharge =
          Number(totalAmountReserveDeal) * (surcharge_percentage_aud / 100);
      } else if (currency === "LKR") {
        reserveDealSurcharge =
          Number(totalAmountReserveDeal) * (surcharge_percentage_lkr / 100);
      }

      const totalReserveAmountWithSurcharge =
        Number(totalAmountReserveDeal) + Number(reserveDealSurcharge);

      setSubTotalAmountReserveDeal(totalAmountReserveDeal.toFixed(2));

      setTotalReserveAmountWithSurcharge(
        totalReserveAmountWithSurcharge.toFixed(2)
      );

      setReserveDealSurcharge(reserveDealSurcharge.toFixed(2));
    }
  }, [
    surcharge_percentage_aud,
    surcharge_percentage_lkr,
    currency,
    exchangeRate,
    totalPriceAud,
  ]);

  const { setFieldValue, values } = useFormikContext();

  // Check if payment plan is enabled for this flight deal
  const isPaymentPlanEnabled = flightDealBookingPending?.flightDeal?.enable_payment_plan === 1;

  // Check if departure date is at least 4 weeks from today
  const isDepartureDateValid = () => {
    if (!departureDate) return false;
    const today = moment().startOf('day');
    const departure = moment(departureDate).startOf('day');
    const weeksUntilDeparture = departure.diff(today, 'weeks');
    return weeksUntilDeparture >= 4;
  };

  const canEnablePaymentPlan = isPaymentPlanEnabled && isDepartureDateValid();

  // Sync with prop changes
  useEffect(() => {
    if (propPaymentType) {
      setPaymentType(propPaymentType);
    }
  }, [propPaymentType]);

  // Auto-switch to full payment if currency changes from AUD while payment plan is selected
  useEffect(() => {
    if (currency !== "AUD" && paymentType === "plan") {
      setPaymentType("full");
      if (onPaymentTypeChange) {
        onPaymentTypeChange("full");
      }
      setFieldValue("paymentType", "full");
    }
  }, [currency, paymentType, onPaymentTypeChange, setFieldValue]);

  // Auto-switch to full payment if payment plan is disabled for this flight deal
  useEffect(() => {
    if (!canEnablePaymentPlan && paymentType === "plan") {
      setPaymentType("full");
      if (onPaymentTypeChange) {
        onPaymentTypeChange("full");
      }
      setFieldValue("paymentType", "full");
    }
  }, [canEnablePaymentPlan, paymentType, onPaymentTypeChange, setFieldValue]);

  // Handle payment plan submission
  const handlePaymentPlanSubmit = async () => {
    try {
      // Validate required fields
      if (!isPaymentPlanFormValid()) {
        console.error("Payment plan form is not valid");
        return;
      }

      // Get customer email from form values
      const customerEmail = (values as any)?.emailAddress;
      if (!customerEmail) {
        console.error("Customer email is required");
        return;
      }

      // Show spinner
      setIsSubmittingPaymentPlan(true);

      // Prepare payload
      const payload = {
        productName: `deal-prd-${generateId()}`,
        productPrice: Math.round(parseFloat(totalPrice) * 100) / 100,
        customerEmail: customerEmail,
        numberOfWeeks: selectedNumberOfWeeks,
        depositAmount: Math.round(parseFloat(depositAmount) * 100) / 100,
        departureDate: departureDate,
        currency: currency.toLowerCase(), // Convert to lowercase for API
        type: PaymentPlanOrderType.DEAL,
      };

      // Validate payload using Yup schema
      try {
        await paymentPlanValidationSchema.validate(payload, { abortEarly: false });
      } catch (validationError) {
        console.error("Validation failed:", JSON.stringify(validationError));
        setIsSubmittingPaymentPlan(false);
        return;
      }

      console.log("Submitting payment plan order:", payload);

      // Make API call
      const response = await createPaymentPlanOrder(payload);
      if (response.orderId) {
        console.log("Payment plan order created successfully:", response);
        // Wait for 3 seconds to ensure data sync is complete in database tables
        setTimeout(async () => {
          console.log("Data synchronization complete, initiating checkout...");
          try {
            // Prepare checkout payload
            const checkoutPayload = {
              email_address: customerEmail,
              order_number: flightDealBookingPending.bookingOrderNumber,
              flight_deal_id: flightDealBookingPending.bookingFlightDealId,
              full_name: (values as any)?.customerName || "",
              mobile: (values as any)?.mobile || "",
              adult: (values as any)?.adult || [],
              child: (values as any)?.child || [],
              infant: (values as any)?.infant || [],
              currency: currency,
              terms: terms,
              payment_plan_id: response.orderId,
            };
            
            // Call initiateFlightDealCheckout
            await initiateFlightDealCheckout(checkoutPayload);
            window.location.href = `${window.origin}/payment-instalment?orderId=${response.orderId}&source=web`;
          } catch (checkoutError) {
            console.error("Error initiating checkout:", checkoutError);
            setIsSubmittingPaymentPlan(false);
          }
        }, 3000);
      } else {
        console.error("Payment plan order creation failed:", response.message);
        setIsSubmittingPaymentPlan(false);
      }
    } catch (error) {
      console.error("Error submitting payment plan:", error);
      setIsSubmittingPaymentPlan(false);
    }
  };

  // Validate deposit amount
  const validateDepositAmount = (value: string) => {
    const depositValue = parseFloat(value);
    const productPrice = parseFloat(totalPrice);

    if (!value || value.trim() === "") {
      return "Deposit amount is required";
    }

    if (isNaN(depositValue) || depositValue <= 0) {
      return "Deposit amount must be positive";
    }

    if (isNaN(productPrice) || productPrice <= 0) {
      return "Invalid product price";
    }

    // Round values to 2 decimal places to handle floating-point precision
    const roundedDepositValue = Math.round(depositValue * 100) / 100;
    const roundedProductPrice = Math.round(productPrice * 100) / 100;
    const minimumDeposit = Math.round(roundedProductPrice * 0.1 * 100) / 100;
    
    if (roundedDepositValue < minimumDeposit) {
      return "Deposit amount must be at least 10% of the product price";
    }

    if (roundedDepositValue > roundedProductPrice) {
      return "Deposit amount cannot exceed the product price";
    }

    return "";
  };

  // Check if payment plan form is valid
  const isPaymentPlanFormValid = () => {
    // Check required fields
    const hasDepositAmount = depositAmount.trim() !== "";
    const hasValidDeposit = depositError === "" && hasDepositAmount;
    const hasTermsAccepted = terms;

    return hasValidDeposit && hasTermsAccepted;
  };

  // Dynamic week options based on departure date
  useEffect(() => {
    const departureDatetime = flightDealBookingPending.flightDeal.departure_datetime;
    
    if (departureDatetime) {
      const today = moment().startOf('day');
      const departure = moment(departureDatetime).startOf('day');
      const diffInWeeks = departure.diff(today, 'weeks');

      // Calculate max weeks: must end 2 weeks before departure
      const maxWeeks = Math.min(20, Math.max(2, diffInWeeks - 2));
      const options = [];

      // Only even numbers between 2 and maxWeeks
      for (let i = 2; i <= maxWeeks; i += 2) {
        options.push(i);
      }

      setWeekOptions(options);

      // If current selection is now invalid, reset it
      if (!options.includes(selectedNumberOfWeeks)) {
        const newValue = options[0] || 2;
        setSelectedNumberOfWeeks(newValue);
        setFieldValue('numberOfWeeks', newValue);
      }
    }
  }, [flightDealBookingPending.flightDeal.departure_datetime, selectedNumberOfWeeks, setFieldValue]);

  return (
    <div className="w-full flex flex-col sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-8 px-0 sm:p-6 xl:p-8">
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
        {flightDealBookingPending.flightDeal.title}
      </h1>
      <div className="w-full border-b border-neutral-100 dark:border-neutral-700" />

      <FlightDetails flightDeal={flightDealBookingPending.flightDeal} />

      <div className="flex flex-col space-y-4">
        <h3 className="text-2xl font-semibold">Number of Passengers</h3>
        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
          <span>Adults</span>
          <span>{flightDealBookingPending.adults}</span>
        </div>
        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
          <span>Children</span>
          <span>{flightDealBookingPending.children}</span>
        </div>
        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
          <span>Infants</span>
          <span>{flightDealBookingPending.infants}</span>
        </div>
        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
      </div>
      {flightDealBookingPending.flightDeal.allow_reserve_deal !== 1 && (
        <div className="flex flex-col space-y-4">
          <h3 className="text-2xl font-semibold">Price Details</h3>
          {currency && (
            <div>
              <div className="flex justify-between font-semibold">
                <span>Sub Total</span>
                <span>
                  {currency} {subTotalPrice}
                </span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Surcharge</span>
                <span>
                  {currency} {surcharge}
                </span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Total Price</span>
                <span>
                  {currency} {totalPrice}
                </span>
              </div>
            </div>
          )}

          {!currency && (
            <div className="flex justify-between font-semibold">
              <span>Please select the currency.</span>
              <span></span>
            </div>
          )}
        </div>
      )}
      {flightDealBookingPending.flightDeal.allow_reserve_deal === 1 && (
        <div>
          <div className="flex flex-col space-y-4">
            <h3 className="text-2xl font-semibold">Price Details</h3>
            {currency && (
              <div>
                <div className="flex justify-between font-semibold">
                  <span>Total Price</span>
                  <span>
                    {currency} {subTotalPrice}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col space-y-4 mt-10">
            <h3 className="text-2xl font-semibold">
              You Pay Today to Reserve Your Deal
            </h3>
            {currency && (
              <div>
                <div className="flex justify-between font-semibold">
                  <span>Sub Total</span>
                  <span>
                    {currency} {subTotalAmountReserveDeal}
                  </span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Surcharge</span>
                  <span>
                    {currency} {reserveDealSurcharge}
                  </span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Reserve Amount</span>
                  <span>
                    {currency} {totalReserveAmountWithSurcharge}
                  </span>
                </div>
              </div>
            )}

            {!currency && (
              <div className="flex justify-between font-semibold">
                <span>Please select the currency.</span>
                <span></span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="relative flex flex-col  space-y-5">
        {flightDealBookingPending.flightDeal.allow_reserve_deal === 1 && (
          <div
            className="bg-blue-200 border-blue-400 text-blue-700 shadow-xl  listingSectionSidebar__wrap"
            role="alert"
          >
            <strong className="font-bold">
              <i className="las la-balance-scale"></i> Conditions
            </strong>
            <div className="block sm:inline" style={{ fontSize: "0.75rem" }}>
              <p>
                <strong>Deposits</strong>
              </p>
              <ul>
                <li>
                  A deposit or deposits, as outlined above, are to be paid as
                  advisory service fees for your booking. In certain cases, full
                  payment is to be made within 10 days from the initial booking.
                </li>
                <li>
                  Please note that all deposits are non-refundable in the event
                  of a change of mind or cancellation by you, except in cases
                  where you have specific rights to a refund or another remedy
                  under the Australian Consumer Law.
                </li>
              </ul>

              <p>
                <strong>Final Payment of Outstanding Balance</strong>
              </p>

              <ul>
                <li>
                  The final payment must be settled no later than 10 days from
                  the moment the booking is made, unless otherwise specified.
                </li>
                <li>
                  Failure to complete the payment for the Travel Product in full
                  by the due date may result in the cancellation of your booking
                  and the forfeiture of any deposit you have previously
                  submitted.
                </li>
              </ul>

              <p>
                <strong>Other Terms & Conditions</strong>
              </p>
              <a href="/terms-and-conditions" target="_blank">
                Click here to view other Terms & Conditions
              </a>
            </div>
          </div>
        )}

        {flightDealBookingPending.flightDeal.allow_reserve_deal !== 1 && (
          <div
            className="bg-blue-200 border-blue-400 text-blue-700 shadow-xl  listingSectionSidebar__wrap"
            role="alert"
          >
            <strong className="font-bold">
              <i className="las la-balance-scale"></i> Terms & Conditions
            </strong>
            <div className="block sm:inline" style={{ fontSize: "0.75rem" }}>
              <a href="/terms-and-conditions" target="_blank">
                Click here to view Terms & Conditions
              </a>
            </div>
          </div>
        )}

        {paymentType === "full" && (
          <>
            <Checkbox
              name="terms"
              label="I have read and agree to the conditions"
              checked={terms}
              onChange={(value: boolean) => {
                handleTermsSelection(value);

                setFieldValue("terms", value);
              }}
            />

            <ErrorMessage name="terms" component="div" className="text-red-500" />
          </>
        )}

        <div className="space-y-3">
          <h4 className="text-lg font-semibold">Payment Options</h4>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="paymentType"
                value="full"
                checked={paymentType === "full"}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setPaymentType(selectedValue);
                  if (onPaymentTypeChange) {
                    onPaymentTypeChange(selectedValue);
                  }
                  setFieldValue("paymentType", selectedValue);
                }}
                className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Pay in full online
              </span>
            </label>
            
            <label className={`flex items-center space-x-3 ${
              currency === "AUD" && canEnablePaymentPlan
                ? "cursor-pointer" 
                : "cursor-not-allowed opacity-50"
            }`}>
              <input
                type="radio"
                name="paymentType"
                value="plan"
                checked={paymentType === "plan"}
                disabled={currency !== "AUD" || !canEnablePaymentPlan}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setPaymentType(selectedValue);
                  if (onPaymentTypeChange) {
                    onPaymentTypeChange(selectedValue);
                  }
                  setFieldValue("paymentType", selectedValue);
                }}
                className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Sign up for a payment plan {
                  currency !== "AUD" 
                    ? "(Only available for AUD)" 
                    : !isPaymentPlanEnabled
                    ? "(Not available for this deal)"
                    : !isDepartureDateValid()
                    ? "(Departure must be at least 4 weeks away)"
                    : ""
                }
              </span>
            </label>
          </div>
        </div>
      </div>

      {paymentType === "full" ? (
        // Existing payment flow for "Pay in full online"
        <>
          {isCreatingCustomer && (
            <ButtonPrimary loading>In progress...</ButtonPrimary>
          )}
          {!isCreatingCustomer && (
            <ButtonPrimary type="submit">Proceed to Payment</ButtonPrimary>
          )}
        </>
      ) : (
        // Payment plan form for "Sign up for a payment plan"
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">Payment Plan Details</h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Departure Date:
              </label>
              <p className="text-base text-neutral-900 dark:text-neutral-100">
                {departureDate || "N/A"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Product Price:
              </label>
              <p className="text-base text-neutral-900 dark:text-neutral-100">
                ${totalPrice ? Number(totalPrice).toFixed(2) : "0.00"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Deposit Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={parseFloat(totalPrice) * 0.1}
                max={parseFloat(totalPrice)}
                step="0.01"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 ${
                  depositError 
                    ? "border-red-500 dark:border-red-500" 
                    : "border-neutral-300 dark:border-neutral-700"
                }`}
                placeholder="0"
                value={depositAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  setDepositAmount(value);
                  const error = validateDepositAmount(value);
                  setDepositError(error);
                  setFieldValue("depositAmount", value);
                }}
                onBlur={(e) => {
                  const error = validateDepositAmount(e.target.value);
                  setDepositError(error);
                }}
              />
              {depositError && (
                <p className="text-xs text-red-500 mt-1">{depositError}</p>
              )}
              {!depositError && totalPrice && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                  Minimum deposit: {currency} {(parseFloat(totalPrice) * 0.1).toFixed(2)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Number of weeks <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedNumberOfWeeks}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setSelectedNumberOfWeeks(value);
                  setFieldValue("numberOfWeeks", value);
                }}
              >
                {weekOptions.map((weeks) => (
                  <option key={weeks} value={weeks}>
                    {weeks} week{weeks > 1 ? 's' : ''}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Checkbox
            name="terms"
            label="I have read and agree to the conditions"
            checked={terms}
            onChange={(value: boolean) => {
              handleTermsSelection(value);

              setFieldValue("terms", value);
            }}
          />

          <ErrorMessage name="terms" component="div" className="text-red-500" />

          {(isCreatingCustomer || isSubmittingPaymentPlan) && (
            <ButtonPrimary loading>
              {isSubmittingPaymentPlan ? "Processing Payment Plan..." : "Submitting Payment Plan Request..."}
            </ButtonPrimary>
          )}
          {!isCreatingCustomer && !isSubmittingPaymentPlan && (
            <ButtonPrimary 
              type="button"
              onClick={handlePaymentPlanSubmit}
              disabled={!isPaymentPlanFormValid()}
            >
              Submit Payment Plan Request
            </ButtonPrimary>
          )}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
